<template>
  <v-app>
    <v-container fluid>
      <v-row v-if="loading && !selectedLcData">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading...</p>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header mb-4">
                LC Costing Entry Form
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form ref="costingForm">
                  <v-row>
                    <!-- Left Column: LC & GRN Selection -->
                    <v-col md="4" cols="12">
                      <v-expansion-panels v-model="panel1" multiple>
                        <v-expansion-panel>
                          <v-expansion-panel-title class="panel-header mb-4">
                            LC & GRN Selection
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <v-autocomplete
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="lc_id.value.value"
                              :items="getLcImportLcMasterList"
                              :item-title="item => `${item.lc_no} - ${item.code || ''}`"
                              item-value="id"
                              :error-messages="lc_id.errorMessage.value"
                              @update:modelValue="loadLcData"
                              clearable
                            >
                              <template #label>
                                Select LC <span class="text-red-accent-4">*</span>
                              </template>
                            </v-autocomplete>

                            <v-autocomplete
                              variant="outlined"
                              density="compact"
                              bg-color="grey-lighten-4"
                              v-model="last_grn_id.value.value"
                              :items="availableGrns"
                              :item-title="item => {
                                const date = item.gnr_date ? formattedDate(item.gnr_date) : 'N/A';
                                const detailsCount = item.gnrDetails?.length || 0;
                                return `GRN #${item.gnr_no || item.id} - ${date}${detailsCount > 0 ? '' : ' (No details)'}`;
                              }"
                              item-value="id"
                              :error-messages="last_grn_id.errorMessage.value"
                              :disabled="!lc_id.value.value"
                              clearable
                            >
                              <template #label>
                                Select GRN (Optional) - {{ availableGrns.length }} available
                              </template>
                            </v-autocomplete>

                            <v-alert
                              v-if="selectedLcData && totalAcceptedQty > 0"
                              type="info"
                              variant="tonal"
                              class="mt-3"
                            >
                              <div class="text-subtitle-2 mb-1">Total Accepted Qty: {{ formatNumber(totalAcceptedQty) }}</div>
                              <div class="text-caption">This will be used to calculate landed cost per unit</div>
                            </v-alert>

                            <v-alert
                              v-if="selectedLcData && totalAcceptedQty === 0"
                              type="warning"
                              variant="tonal"
                              class="mt-3"
                            >
                              No approved GRN found for this LC. Please create and post a GRN first.
                            </v-alert>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>

                    <!-- Right Column: Costing Details -->
                    <v-col md="8" cols="12">
                      <v-expansion-panels v-model="panel2" multiple>
                        <v-expansion-panel>
                          <v-expansion-panel-title class="panel-header mb-4">
                            Costing Details
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <v-row>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="cif_value.value.value"
                                  :error-messages="cif_value.errorMessage.value"
                                  label="CIF Value"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="freight.value.value"
                                  :error-messages="freight.errorMessage.value"
                                  label="Freight"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="insurance.value.value"
                                  :error-messages="insurance.errorMessage.value"
                                  label="Insurance"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="customs_duty.value.value"
                                  :error-messages="customs_duty.errorMessage.value"
                                  label="Customs Duty"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="vat.value.value"
                                  :error-messages="vat.errorMessage.value"
                                  label="VAT"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="cnf_charge.value.value"
                                  :error-messages="cnf_charge.errorMessage.value"
                                  label="CNF Charge"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="port_charge.value.value"
                                  :error-messages="port_charge.errorMessage.value"
                                  label="Port Charge"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                  variant="outlined"
                                  density="compact"
                                  bg-color="grey-lighten-4"
                                  v-model="bank_charge.value.value"
                                  :error-messages="bank_charge.errorMessage.value"
                                  label="Bank Charge"
                                  type="number"
                                  :min="0"
                                  :step="0.01"
                                  @update:model-value="calculateTotals"
                                ></v-text-field>
                              </v-col>
                            </v-row>

                            <!-- Summary Card -->
                            <v-card variant="outlined" class="mt-3">
                              <v-card-title class="text-subtitle-1 bg-grey-lighten-4">
                                Costing Summary
                              </v-card-title>
                              <v-card-text>
                                <v-row>
                                  <v-col cols="12" md="6">
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Accepted Qty:</strong> {{ formatNumber(totalAcceptedQty) }}
                                    </div>
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Total Landed Cost:</strong> {{ formatCurrency(totalLandedCost) }}
                                    </div>
                                  </v-col>
                                  <v-col cols="12" md="6">
                                    <div class="d-flex justify-space-between mb-2">
                                      <strong>Landed Cost Per Unit:</strong> {{ formatCurrency(landedCostPerUnit) }}
                                    </div>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>

                            <!-- Action Buttons -->
                            <v-row class="mt-3">
                              <v-col cols="12">
                                <div class="d-flex justify-end" style="gap: 12px;">
                                  <v-btn
                                    class="bg-grey-darken-1"
                                    variant="flat"
                                    type="button"
                                    :loading="submitting && currentStatus === 0"
                                    :disabled="submitting && currentStatus !== 0 || totalAcceptedQty === 0"
                                    @click="submitForm(0)"
                                  >
                                    DRAFT
                                  </v-btn>
                                  <v-btn
                                    class="bg-primary"
                                    variant="flat"
                                    type="button"
                                    :loading="submitting && currentStatus === 1"
                                    :disabled="submitting && currentStatus !== 1 || totalAcceptedQty === 0"
                                    @click="submitForm(1)"
                                  >
                                    POST
                                  </v-btn>
                                </div>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formattedDate from "@/plugins/dateFormatter";
import { formatDateView, formatDateDash } from "@/plugins/helpers";
import { useField, useForm } from "vee-validate";
import { ref, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import * as yup from "yup";

export default {
  name: "LcImportLcCosting",
  methods: {
    formattedDate,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const panel = ref([0]);
    const panel1 = ref([0]);
    const panel2 = ref([0]);
    const loading = ref(false);
    const submitting = ref(false);
    const currentStatus = ref(0);
    const selectedLcData = ref(null);
    const availableGrns = ref([]);

    const validationSchema = yup.object({
      lc_id: yup.number().required("LC is required"),
      cif_value: yup.number().min(0, "CIF Value must be 0 or greater").nullable(),
      freight: yup.number().min(0, "Freight must be 0 or greater").nullable(),
      insurance: yup.number().min(0, "Insurance must be 0 or greater").nullable(),
      customs_duty: yup.number().min(0, "Customs Duty must be 0 or greater").nullable(),
      vat: yup.number().min(0, "VAT must be 0 or greater").nullable(),
      cnf_charge: yup.number().min(0, "CNF Charge must be 0 or greater").nullable(),
      port_charge: yup.number().min(0, "Port Charge must be 0 or greater").nullable(),
      bank_charge: yup.number().min(0, "Bank Charge must be 0 or greater").nullable(),
    });

    const { handleSubmit, resetForm: resetFormValidation } = useForm({
      validationSchema,
    });

    const lc_id = useField("lc_id");
    const last_grn_id = useField("last_grn_id");
    const cif_value = useField("cif_value");
    const freight = useField("freight");
    const insurance = useField("insurance");
    const customs_duty = useField("customs_duty");
    const vat = useField("vat");
    const cnf_charge = useField("cnf_charge");
    const port_charge = useField("port_charge");
    const bank_charge = useField("bank_charge");

    const getLcImportLcMasterList = computed(() => {
      return store.getters.getLcImportLcMasterList || [];
    });

    const totalAcceptedQty = computed(() => {
      if (!selectedLcData.value) return 0;
      
      console.log('🔍 Calculating totalAcceptedQty');
      console.log('  - Available GRNs:', availableGrns.value.length);
      console.log('  - Selected GRN ID:', last_grn_id.value.value);
      
      // If a specific GRN is selected, use its accepted qty
      if (last_grn_id.value.value) {
        const selectedGrn = availableGrns.value.find(g => g.id === last_grn_id.value.value);
        console.log('  - Selected GRN:', selectedGrn);
        if (selectedGrn) {
          // Check both camelCase and snake_case
          const details = selectedGrn.gnrDetails || selectedGrn.gnr_details || [];
          console.log('  - GRN Details:', details);
          if (Array.isArray(details) && details.length > 0) {
            const total = details.reduce((sum, item) => {
              const qty = parseFloat(item.accepted_qty || item.acceptedQty || 0);
              console.log(`    - Item accepted_qty: ${qty}`);
              return sum + qty;
            }, 0);
            console.log('  - Total Accepted Qty (selected):', total);
            return total;
          }
        }
        console.log('  - No details found for selected GRN, returning 0');
        return 0;
      }
      
      // Otherwise, sum all GRNs for this LC
      const total = availableGrns.value.reduce((sum, grn) => {
        const details = grn.gnrDetails || grn.gnr_details || [];
        if (Array.isArray(details) && details.length > 0) {
          const grnQty = details.reduce((qtySum, item) => {
            return qtySum + parseFloat(item.accepted_qty || item.acceptedQty || 0);
          }, 0);
          console.log(`  - GRN ${grn.gnr_no || grn.id} qty: ${grnQty}`);
          return sum + grnQty;
        }
        return sum;
      }, 0);
      
      console.log('  - Total Accepted Qty (all GRNs):', total);
      return total;
    });

    const totalLandedCost = computed(() => {
      const cif = parseFloat(cif_value.value.value || 0);
      const freightVal = parseFloat(freight.value.value || 0);
      const insuranceVal = parseFloat(insurance.value.value || 0);
      const customsDuty = parseFloat(customs_duty.value.value || 0);
      const vatVal = parseFloat(vat.value.value || 0);
      const cnfCharge = parseFloat(cnf_charge.value.value || 0);
      const portCharge = parseFloat(port_charge.value.value || 0);
      const bankCharge = parseFloat(bank_charge.value.value || 0);
      
      return cif + freightVal + insuranceVal + customsDuty + vatVal + cnfCharge + portCharge + bankCharge;
    });

    const landedCostPerUnit = computed(() => {
      if (totalAcceptedQty.value <= 0) return 0;
      return totalLandedCost.value / totalAcceptedQty.value;
    });

    const loadLcData = async () => {
      if (!lc_id.value.value) {
        selectedLcData.value = null;
        availableGrns.value = [];
        last_grn_id.value.value = null;
        return;
      }

      loading.value = true;
      try {
        await store.dispatch("getLcImportLcMasterById", lc_id.value.value);
        const lcData = store.getters.getEditingLcImportLcMaster;
        selectedLcData.value = lcData;

        // Load GRNs for this LC
        await store.dispatch("getLcImportGnrList");
        const allGrns = store.getters["getLcImportGnrList"];
        
        console.log('🔍 Debug - All GRNs loaded:', allGrns.length);
        console.log('🔍 Debug - LC ID selected:', lc_id.value.value);
        
        // Filter: only posted GRNs (status = 1) for this LC
        const filteredGrns = allGrns.filter(
          g => g.lc_id === lc_id.value.value && g.status === 1
        );
        
        console.log('🔍 Debug - Filtered GRNs (status=1):', filteredGrns.length);
        filteredGrns.forEach(grn => {
          console.log(`  - GRN ID: ${grn.id}, GNR No: ${grn.gnr_no}, Status: ${grn.status}, Approval: ${grn.approval_status || 'N/A'}`);
        });
        
        // Load details for each GRN
        availableGrns.value = [];
        for (const grn of filteredGrns) {
          try {
            console.log(`🔍 Loading details for GRN ${grn.gnr_no} (ID: ${grn.id})`);
            await store.dispatch("getLcImportGnrById", grn.id);
            const grnWithDetails = store.getters["getEditingLcImportGnr"];
            
            if (grnWithDetails) {
              console.log(`  ✓ GRN ${grn.gnr_no} loaded successfully`);
              console.log(`  📦 Full GRN object keys:`, Object.keys(grnWithDetails));
              console.log(`  📦 Full GRN object:`, grnWithDetails);
              
              // Check for both camelCase and snake_case
              const details = grnWithDetails.gnrDetails || grnWithDetails.gnr_details || grnWithDetails.gnrDetails || [];
              console.log(`  - Has gnrDetails (camelCase):`, !!grnWithDetails.gnrDetails);
              console.log(`  - Has gnr_details (snake_case):`, !!grnWithDetails.gnr_details);
              console.log(`  - Details (using found property):`, details);
              console.log(`  - Details count:`, Array.isArray(details) ? details.length : 0);
              
              // Store details in a consistent property name
              if (!grnWithDetails.gnrDetails && grnWithDetails.gnr_details) {
                grnWithDetails.gnrDetails = grnWithDetails.gnr_details;
              }
              
              // Add GRN even if it has no details (we'll show it but warn)
              if (grnWithDetails.gnrDetails && Array.isArray(grnWithDetails.gnrDetails) && grnWithDetails.gnrDetails.length > 0) {
                console.log(`  ✓ GRN ${grn.gnr_no} has ${grnWithDetails.gnrDetails.length} details - adding to list`);
                availableGrns.value.push(grnWithDetails);
              } else {
                console.warn(`  ⚠ GRN ${grn.gnr_no} has no details - adding anyway for debugging`);
                // Ensure gnrDetails is at least an empty array
                if (!grnWithDetails.gnrDetails) {
                  grnWithDetails.gnrDetails = [];
                }
                // Add it anyway so user can see it exists
                availableGrns.value.push(grnWithDetails);
              }
            } else {
              console.warn(`  ✗ GRN ${grn.gnr_no} - no data returned from API`);
            }
          } catch (error) {
            console.error(`  ✗ Failed to load GRN ${grn.gnr_no} details:`, error);
            console.error(`  ✗ Error details:`, error.response?.data || error.message);
            // Add the GRN without details so user can see it exists
            availableGrns.value.push(grn);
          }
        }
        
        console.log('🔍 Final available GRNs count:', availableGrns.value.length);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load LC data");
      } finally {
        loading.value = false;
      }
    };

    const calculateTotals = () => {
      // Totals are computed, so they update automatically
    };

    const formatNumber = (num) => {
      if (!num && num !== 0) return "0.00";
      return Number(num).toFixed(2);
    };

    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return "0.00";
      const currencySymbol = selectedLcData.value?.currency?.symbol || '';
      return currencySymbol ? `${currencySymbol} ${Number(amount).toFixed(2)}` : Number(amount).toFixed(2);
    };

    const submitForm = async (btnStatus) => {
      currentStatus.value = btnStatus;

      if (!lc_id.value.value) {
        toast.error("Please select an LC");
        return;
      }

      if (totalAcceptedQty.value <= 0) {
        if (availableGrns.value.length === 0) {
          toast.error("No posted GRN found for this LC. Please create and post a GRN first.");
        } else {
          toast.error("No GRN with accepted quantity found for this LC. Please ensure your GRN has items with accepted quantity > 0.");
        }
        return;
      }

      submitting.value = true;
      try {
        const formData = {
          lc_id: lc_id.value.value,
          cif_value: parseFloat(cif_value.value.value || 0),
          freight: parseFloat(freight.value.value || 0),
          insurance: parseFloat(insurance.value.value || 0),
          customs_duty: parseFloat(customs_duty.value.value || 0),
          vat: parseFloat(vat.value.value || 0),
          cnf_charge: parseFloat(cnf_charge.value.value || 0),
          port_charge: parseFloat(port_charge.value.value || 0),
          bank_charge: parseFloat(bank_charge.value.value || 0),
          status: btnStatus,
          last_grn_id: last_grn_id.value.value || null,
        };

        await store.dispatch('lcImportLcCosting/saveUpdateLcImportLcCosting', formData);
        toast.success(btnStatus === 0 ? "LC Costing saved as draft successfully" : "LC Costing posted successfully");
        router.push({ name: 'lc-import-lc-costing-records' });
      } catch (error) {
        toast.error(error.response?.data?.message || error.response?.data?.error || `Failed to ${btnStatus === 0 ? 'save draft' : 'post'} LC Costing`);
      } finally {
        submitting.value = false;
      }
    };

    const loadExistingData = async () => {
      if (route.params.id) {
        loading.value = true;
        try {
          await store.dispatch("lcImportLcCosting/getLcImportLcCostingById", route.params.id);
          const costingData = store.getters["lcImportLcCosting/getEditingLcImportLcCosting"];
          
          if (costingData) {
            lc_id.value.value = costingData.lc_id;
            last_grn_id.value.value = costingData.last_grn_id;
            cif_value.value.value = costingData.cif_value;
            freight.value.value = costingData.freight;
            insurance.value.value = costingData.insurance;
            customs_duty.value.value = costingData.customs_duty;
            vat.value.value = costingData.vat;
            cnf_charge.value.value = costingData.cnf_charge;
            port_charge.value.value = costingData.port_charge;
            bank_charge.value.value = costingData.bank_charge;
            
            await loadLcData();
          }
        } catch (error) {
          toast.error("Failed to load costing data");
        } finally {
          loading.value = false;
        }
      }
    };

    const resetForm = () => {
      resetFormValidation();
      selectedLcData.value = null;
      availableGrns.value = [];
      lc_id.value.value = null;
      last_grn_id.value.value = null;
      cif_value.value.value = null;
      freight.value.value = null;
      insurance.value.value = null;
      customs_duty.value.value = null;
      vat.value.value = null;
      cnf_charge.value.value = null;
      port_charge.value.value = null;
      bank_charge.value.value = null;
    };

    onMounted(async () => {
      await store.dispatch('getLcImportLcMasterList');
      await store.dispatch('getLcImportGnrList');
      await loadExistingData();
    });

    return {
      panel,
      panel1,
      panel2,
      loading,
      submitting,
      currentStatus,
      selectedLcData,
      availableGrns,
      lc_id,
      last_grn_id,
      cif_value,
      freight,
      insurance,
      customs_duty,
      vat,
      cnf_charge,
      port_charge,
      bank_charge,
      getLcImportLcMasterList,
      totalAcceptedQty,
      totalLandedCost,
      landedCostPerUnit,
      loadLcData,
      calculateTotals,
      formatNumber,
      formatCurrency,
      submitForm,
      resetForm,
    };
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

